import { Box, Button, Card, CardHeader, TableContainer, TableBody, Table, TableHead, TableRow, Container, IconButton, MenuItem, Stack } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldDormancySettings } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";

import PageHeader from "src/components/page-header";
import useModelSavingData from "src/hooks/use-model-saving-data";
import { usePrevious } from "src/hooks/use-previous";
import Iconify from "src/components/iconify/iconify";
import TruxSelectField from "src/components/inputs/trux-select-field";
import SimpleDialog from "src/components/simple-dialog";
import DisplayErrors from "src/components/display-errors";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxCheckboxField from "src/components/inputs/trux-checkbox-field";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import { TruxTableCell } from "src/components/trux-table-cell";
import { TruxDeleteTableCell } from "src/components/trux-delete-table-cell";

type PageModel = {
  eldDormancySettings?: EldDormancySettings,
}

export default function Page() {
  const settings = useSettingsContext();

  const dataLoader = useCallback(() => ({
    eldDormancySettings: axios.get(endpoints.eldDormancySettings.current).then(response => response.data),
  }), []);

  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      alert(error);
    }
  }, [loading, error]);

  const previousEldDormancySettings = usePrevious(data?.eldDormancySettings)

  const eldDormancySettingsModel = useModelSavingData<EldDormancySettings>(
    endpoints.eldDormancySettings.update
  );

  useEffect(() => {
    if (data?.eldDormancySettings && data.eldDormancySettings !== previousEldDormancySettings) {
      eldDormancySettingsModel.set(data.eldDormancySettings)
    }
  }, [previousEldDormancySettings, data?.eldDormancySettings, eldDormancySettingsModel]);

  const [isOpenSaveChanges, setIsOpenSaveChanges] = useState(false);

  const updateEldDormancySettings = useCallback(() => {
    eldDormancySettingsModel.save().then((response) => {
      if (response) {
        // we should apply the response back to the load model
        updateData("eldDormancySettings")(response.data as any)

        enqueueSnackbar("Successfully updated the Dormancy Settings", { variant: "success" })
        setIsOpenSaveChanges(false);
      }
    })
  }, [eldDormancySettingsModel, updateData]);


  const updateEldDormancySettingsButton = <Button
    onClick={() => setIsOpenSaveChanges(true)}
    variant="outlined"
    color="info"
    startIcon={<Iconify icon="basil:save-solid" />}
  >
    Save Changes
  </Button>;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {eldDormancySettingsModel.model && <Box sx={{ display: "flex", flexGrow: 1 }}>

          <Stack direction="column" flexGrow={1} gap={2}>
            <PageHeader title="Settings">
              {updateEldDormancySettingsButton}
            </PageHeader>

            <Card>
              <CardHeader title="General" />

              <Stack spacing={2} sx={{ p: 2 }}>
                <TruxFormField label="Report Dormancy">
                  <TruxCheckboxField modelSavingData={eldDormancySettingsModel} field="reportDormancy" />
                </TruxFormField>

                {eldDormancySettingsModel.model?.reportDormancy && <>
                  <TruxFormField label="Min Duration Days">
                    <TruxSelectField
                      modelSavingData={eldDormancySettingsModel}
                      field="dormancyDurationDays"
                    >
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={7}>7</MenuItem>
                      <MenuItem value={14}>14</MenuItem>
                      <MenuItem value={21}>21</MenuItem>
                      <MenuItem value={28}>28</MenuItem>
                    </TruxSelectField>
                  </TruxFormField>

                  <Stack>
                    <Box sx={{ marginLeft: -2, marginRight: -2 }}>
                      <TableContainer sx={{ maxHeight: 440 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {eldDormancySettingsModel.model.emails.length > 1 && <TruxTableCell sx={{ maxWidth: "30px" }} />}
                              <TruxTableCell>
                                Email
                              </TruxTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {eldDormancySettingsModel.model.emails.map((email, index) => <TableRow key={index}>
                              {(eldDormancySettingsModel?.model?.emails.length || 0) > 1 &&
                                <TruxDeleteTableCell>
                                  <IconButton color="error" onClick={() => {
                                    eldDormancySettingsModel.update((model) => {
                                      model.emails.splice(index, 1);
                                    })
                                  }}>
                                    <Iconify icon="solar:trash-bin-trash-bold" />
                                  </IconButton>
                                </TruxDeleteTableCell>
                              }
                              <TruxTableCell>
                                <TruxSimpleTextField value={email} onChange={(val) => {
                                  eldDormancySettingsModel.update((model) => {
                                    model.emails[index] = val;
                                  })
                                }} />
                              </TruxTableCell>
                            </TableRow>)}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                    <Box>
                      <Button size="small" variant="soft" onClick={() => {
                        eldDormancySettingsModel.update((model) => {
                          model.emails.push("");
                        })
                      }} startIcon={<Iconify icon="mingcute:add-line" />}>
                        Add Email
                      </Button>
                    </Box>
                  </Stack>
                </>}
              </Stack>
            </Card>
          </Stack>
        </Box>}
      </DisplayPageData >

      {
        eldDormancySettingsModel.model &&
        <SimpleDialog
          title="Save Eld Dormancy Settings"
          open={isOpenSaveChanges}
          onCancel={() => {
            setIsOpenSaveChanges(false);
            eldDormancySettingsModel.clearErrors();
          }}
          onSubmit={updateEldDormancySettings}
          submitDisabled={eldDormancySettingsModel.loading}
          submitButton="Save Settings"
          submitButtonIcon={<Iconify icon="basil:save-solid" />}
          submitButtonColor="info">

          <DisplayErrors savingData={eldDormancySettingsModel} />

          Are you sure you would like to save your changes?
        </SimpleDialog>
      }
    </Container >
  );
}
